<script setup>
import i18n from '@/i18n';
import _ from 'lodash';
import { ref, onMounted, getCurrentInstance } from 'vue';
import ActionTabItem from './components/AgentTabItem.vue';
import ActionAgentItem from './components/ActionAgentItem.vue';
import { computed } from 'vue';

const t = (text) => {
  return i18n.global.t(text);
};

const { $http } = getCurrentInstance().appContext.config.globalProperties;

defineOptions({
  name: 'MontargetAgent'
});

const AgentConst = {
  Performance: {
    label: t('tab.performance'),
    key: 'metric'
  },
  Log: {
    label: t('tab.log'),
    key: 'log'
  },
  Win: {
    label: t('tab.winEvent'),
    key: 'winlog'
  }
};

const LogActionConst = {
  Add: {
    text: t('label.addLog'),
    value: 'addLog'
  },
  Delete: {
    text: t('label.deleteLog'),
    value: 'deleteLog'
  }
};

const props = defineProps({
  params: {
    type: Object,
    required: true
  },
  productInfoAttrs: {
    type: Array,
    required: true
  },
  managedYn: {
    type: String,
    required: true
  }
});

const commomAction = {
  installCmd: '',
  startCmd: '',
  stopCmd: '',
  deleteCmd: '',
  updateCmd: ''
};

const agentData = ref({
  [AgentConst.Performance.key]: commomAction,
  [AgentConst.Log.key]: commomAction,
  [AgentConst.Win.key]: commomAction
});

const loading = ref(false);

const agentTabKey = ref(AgentConst.Performance.key);
const logAction = ref(LogActionConst.Add.value);
const agentTabs = ref(AgentConst);
const logName = ref('');
const logPath = ref('');
const logURL = ref('');
const isEnoughAttrs = ref(false);

const showLogSetup = computed(() => agentTabKey.value === AgentConst.Log.key);

const showLogPath = computed(() => logAction.value !== LogActionConst.Delete.value);

const enableCreateCommand = computed(() => {
  const addLogInvalid = [
    !isValidLogPath.value,
    !isValidateLogPathSpecial.value,
    !isValidCommonFilePath.value,
    !isValidCommonLogName.value
  ];
  switch (logAction.value) {
    case LogActionConst.Add.value:
      return addLogInvalid.every((invalid) => !!invalid);
    case LogActionConst.Delete.value:
      return !isValidCommonLogName.value;
    default:
      return true;
  }
});

const isValidateLogPathSpecial = computed(() => {
  return /^[*]/g.test(logPath.value.toLowerCase());
});

const isValidLogPath = computed(() => {
  const regex = /(\/var\/log\/)|^(var\/log$)|^(\/var\/log\/)|^(var\/log\/)|(\/var\/log$)/g;
  if (props.managedYn === 'Y') {
    return regex.test(logPath.value.toLowerCase());
  }
  return false;
});
const isValidCommonLogName = computed(() => {
  const regex = /[^A-Za-z0-9._-]/g;
  return !logName.value || regex.test(logName.value);
});

const checkoutOsWindows = computed(() => {
  const osInfo = props.productInfoAttrs.find((item) => item.attrName === 'osType');
  return (osInfo?.attrValue || '').toLowerCase().includes('windows');
});

const isValidCommonFilePath = computed(() => {
  let regex = /[<>$#&()@!\\;']|\/{2,}/g;
  if (checkoutOsWindows.value) {
    regex = /[<>$#&()@!;']|\/{2,}|\\{2,}/g;
  }
  return !logPath.value || regex.test(logPath.value);
});

const getTextGuide = computed(() =>
  checkoutOsWindows.value ? t('label.agentPathGuideWindows') : t('label.agentPathGuide')
);

onMounted(async () => {
  const productResourceId = props.params.productResourceId;
  if (!productResourceId) return;

  checkWinLogTab();
  loading.value = true;

  const res = await $http.agent.checkAgentTab(productResourceId);
  isEnoughAttrs.value = res.paas;
  if (isEnoughAttrs.value) await getCmds();

  loading.value = false;
});

const getCmds = async () => {
  const { productResourceId } = props.params;
  const agentKeys = Object.keys(agentData.value);
  const arrPrm = agentKeys.map((agentType) =>
    $http.agent.getManualAgent(productResourceId, agentType)
  );
  await Promise.all(arrPrm).then((res) => {
    agentKeys.forEach((key, idx) => {
      let item = res[idx];
      if (key === AgentConst.Win.key) {
        agentData.value[key] = {
          ...item,
          startCmd: item.winLogStartCmd,
          stopCmd: item.winLogStopCmd,
          updateCmd: item.winLogUpdateCmd,
          installCmd: item.winLogInstallCmd,
          deleteCmd: item.winLogDeleteCmd
        };
      } else {
        agentData.value[key] = { ...item };
      }
    });
  });
};

const checkWinLogTab = () => {
  if (!checkoutOsWindows.value) {
    agentTabs.value = _.pick(AgentConst, ['Performance', 'Log']);
    agentData.value = _.pick(agentData.value, [AgentConst.Performance.key, AgentConst.Log.key]);
  }
};

const onChangeTab = (tabKey) => {
  agentTabKey.value = tabKey;
  resetLog();
};

const resetLog = (action = LogActionConst.Add.value) => {
  logURL.value = '';
  logName.value = '';
  logPath.value = '';
  logAction.value = action;
};

const handleChangeLogAction = (action) => {
  resetLog(action);
};

const validateCommonLogName = () => {
  if (!logName.value) return t('message.requiredInput');

  return !isValidCommonLogName.value || t('message.errorValidateInput');
};

const validateCommonLogPath = () => {
  if (!logPath.value) return t('message.requiredInput');

  return !isValidCommonFilePath.value || t('message.errorValidateInput');
};

const validateLogPath = () => {
  return !isValidLogPath.value || t('page.log.eventLogFilePath');
};
const validateSpecialLogPath = () => {
  return !isValidateLogPathSpecial.value || t('page.log.eventLogFilePathSpecial');
};

const handleCommand = () => {
  const logData = agentData.value[AgentConst.Log.key];

  switch (logAction.value) {
    case LogActionConst.Add.value:
      return (logURL.value = logData.customLogAddCmd
        .replace(/{itemName}/g, logName.value)
        .replace(/{itemPath}/g, logPath.value));
    case LogActionConst.Delete.value:
      return (logURL.value = logData.customLogRemoveCmd.replace(/{itemName}/g, logName.value));
    default:
      break;
  }
};
</script>

<template>
  <div v-if="!loading" class="agent-container">
    <lego-segment-wrapper
      primary
      :small="small"
      :modelValue="agentTabKey"
      @update:modelValue="onChangeTab"
    >
      <lego-segment-button v-for="(value, key, idx) in agentTabs" :key="idx" :value="value.key"
        >{{ value.label }}
      </lego-segment-button>
    </lego-segment-wrapper>

    <div class="agent-container__tabs">
      <template v-for="(value, key, index) in AgentConst" :key="index">
        <ActionTabItem
          v-if="agentTabKey === value.key"
          :data="agentData[value.key]"
          :isEnoughAttrs="isEnoughAttrs"
        />
      </template>

      <template v-if="showLogSetup">
        <div class="log-setup">
          <div>
            <c-label class="mb-8" :weight="500" :lineHeight="16">{{ t('label.action') }}</c-label>
            <lego-dropdown
              class="log-action"
              v-model="logAction"
              :items="[LogActionConst.Add, LogActionConst.Delete]"
              @update:modelValue="handleChangeLogAction"
            />
          </div>
          <div class="flex-1">
            <c-label class="mb-8" required :weight="500" :lineHeight="16"
              >{{ t('label.fileName') }}
            </c-label>
            <lego-text-field
              v-model="logName"
              counter="50"
              :isCounter="false"
              :rules="[validateCommonLogName]"
              :guide="$t('label.agentNameGuide')"
            />
          </div>
          <div v-if="showLogPath" class="flex-1">
            <c-label class="mb-8" required :weight="500" :lineHeight="16"
              >{{ t('label.filePath') }}
            </c-label>
            <lego-text-field
              v-model="logPath"
              counter="256"
              :isCounter="false"
              :guide="getTextGuide"
              :rules="[validateCommonLogPath, validateLogPath, validateSpecialLogPath]"
            />
          </div>
          <div>
            <lego-button
              style="font-weight: 400; margin-top: 24px"
              medium
              main
              :disabled="!enableCreateCommand"
              @click="handleCommand"
              >{{ t('label.createCommand') }}
            </lego-button>
          </div>
        </div>
        <ActionAgentItem
          v-if="logURL"
          style="margin-top: 16px"
          hiddenLabel
          :url="logURL"
          :actionName="t(`label.${logAction.toString()}`)"
          :isEnoughAttrs="isEnoughAttrs"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.agent-container {
  padding: 16px 24px;

  ::v-deep(.lego-segment-button) {
    width: 132px !important;
  }

  &__tabs {
    margin-top: 16px;

    .log-setup {
      border-top: 1px solid #cfd2d8;
      padding-top: 16px;
      display: flex;
      gap: 16px;
    }

    .log-action {
      width: 200px;
    }

    .flex-1 {
      flex: 1;
    }

    .mb-8 {
      margin-bottom: 8px;
    }
  }
}
</style>
